import * as Sentry from "@sentry/react-native";
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  Method,
} from "axios";
import { DocumentPickerAsset } from "expo-document-picker";
import { router } from "expo-router";
import { camelize } from "fast-case";
import { t } from "i18next";

import { envConfig } from "../../environment.config";

import {
  dispatchSessionExpiredAlertState,
  dispatchApplicationReadyForSubmit,
} from "@/context/store";
import { fieldsOnboarding } from "@/utils/EnumsOnboarding";
import { getToken } from "@/utils/Helpers";
import { toastInstance } from "@/utils/ToastConfig";
import { decodeObjectValues } from "@/utils/decode/Decode";
import {
  locationsUrls,
  throwPostResponseUrls,
  throwGetResponseUrls,
  ignoreErrorPostUrls,
  ignoreErrorGetUrls,
} from "@/utils/urls/Urls";

interface Headers {
  [key: string]: string;
}

export const baseURL = envConfig.API_URL;
export const baseLayer2AssetNet = envConfig.BASE_NET;

function useValidateFieldName(fieldName: string) {
  if (fieldName.includes(".")) {
    const parts = fieldName.split(".");
    const lastPart = parts[parts.length - 1];
    return camelize(lastPart);
  } else {
    return camelize(fieldName);
  }
}

const handleApplicationStatus = async (
  status: string,
  applicationIndividualErrors?: any
) => {
  let fieldErrors: any = undefined;
  let missingBo = false;

  if (
    applicationIndividualErrors &&
    (applicationIndividualErrors.validation_errors ||
      applicationIndividualErrors.application_individual_errors)
  ) {
    fieldErrors =
      applicationIndividualErrors.validation_errors.length === 0 ||
      !applicationIndividualErrors.validation_errors
        ? applicationIndividualErrors.application_individual_errors[0]
            .validation_errors
        : applicationIndividualErrors.validation_errors;
  }

  if (
    applicationIndividualErrors &&
    applicationIndividualErrors.application_errors &&
    applicationIndividualErrors.application_errors.length > 0 &&
    applicationIndividualErrors.application_errors !== null &&
    applicationIndividualErrors.application_errors !== undefined
  ) {
    const applicationErrors = applicationIndividualErrors.application_errors;
    const missingBoError = applicationErrors.find(
      (error: any) =>
        error === "MISSING_CONTROL_PERSON" ||
        error === "MISSING_BENEFICIAL_OWNER"
    );
    missingBo = !!missingBoError;
  }

  const hasChangesRequested = fieldErrors?.some((validationError: any) =>
    fieldsOnboarding.includes(useValidateFieldName(validationError.field_name))
  );

  switch (status) {
    case "REJECTED":
      router.replace("/rejected");
      break;
    case "CHANGES_REQUESTED":
      if (hasChangesRequested) {
        router.replace("/review");
      } else if (missingBo) {
        router.replace("/corporation/addindividual");
      } else {
        router.replace("/documents");
      }
      break;
    case "APPROVED":
      router.replace("/approved");
      break;
    case "COMPLETED":
      router.replace("/approved");
      break;
    case "INCOMPLETE":
      if (hasChangesRequested) {
        router.replace("/review");
      } else if (missingBo) {
        router.replace("/corporation/addindividual");
      } else {
        router.replace("/documents");
      }
      break;
    case "READY_FOR_SUBMISSION": {
      dispatchApplicationReadyForSubmit();
      break;
    }
    default:
      break;
  }
};

const responseStatus = (error: any, url?: string, method?: string) => {
  if (ignoreErrorPostUrls.includes(url) && method.toLowerCase() === "post") {
    return;
  } else if (
    ignoreErrorGetUrls.includes(url) &&
    method.toLowerCase() === "get"
  ) {
    return;
  } else if (
    url === "/login" &&
    error.response.data.message === "OTP_REQUIRED"
  ) {
    return;
  } else if (
    (error.response.status === 403 || error.response.status === 401) &&
    url !== "/login"
  ) {
    dispatchSessionExpiredAlertState();
    router.replace("/");
    return;
  }

  const errorMessagesConstraints: string[] = [];
  const errorMessagesDescriptions: string[] = [];

  const extractErrorMessages = (errorObj: any): string[] => {
    const errorMessages: string[] = [];

    if (errorObj.constraints) {
      const constraintsErros = Object.values(errorObj.constraints) as string[];
      errorMessages.push(...constraintsErros);
    }

    if (
      errorObj.children.length > 0 &&
      errorObj.children !== undefined &&
      errorObj.children !== null
    ) {
      errorObj.children.forEach((child: any) => {
        const childErrors = extractErrorMessages(child);
        errorMessages.push(...childErrors);
      });
    }

    return errorMessages;
  };

  error.response.data.errors?.forEach((err: any) => {
    if (err.description) {
      errorMessagesDescriptions.push(err.description);
    } else if (err.children.length > 0) {
      err.children.forEach((child: any) => {
        const childErrors = extractErrorMessages(child);
        errorMessagesConstraints.push(...childErrors);
      });
    } else if (err.constraints) {
      for (const constraintKey in err.constraints) {
        errorMessagesConstraints.push(err.constraints[constraintKey]);
      }
    }
  });

  if (Array.isArray(error.response.data)) {
    error.response.data.forEach((err: any) => {
      if (err && err.description) {
        errorMessagesDescriptions.push(err.description);
      }
    });
  }

  if (error.response && error.response.data.errors) {
    const errorMessages = error.response.data.errors.map(
      (err: any) => err.description
    );

    if (
      errorMessages &&
      errorMessages.length > 0 &&
      errorMessages.every((msg: any) => msg !== undefined)
    ) {
      toastInstance("error", errorMessages.join("\n"));
    } else if (errorMessagesConstraints.length > 0) {
      errorMessagesConstraints.forEach((msg) => {
        toastInstance("error", msg);
      });
    } else if (errorMessagesDescriptions.length > 0) {
      errorMessagesDescriptions.forEach((msg) => {
        toastInstance("error", msg);
      });
    }
  } else if (error.response && error.response.data.message) {
    toastInstance("error", error.response.data.message);
  } else {
    if (errorMessagesDescriptions.length > 0) {
      errorMessagesDescriptions.forEach((msg) => {
        toastInstance("error", msg);
      });
    } else if (errorMessagesConstraints.length > 0) {
      errorMessagesConstraints.forEach((msg) => {
        toastInstance("error", msg);
      });
    } else {
      toastInstance("error", t("common:error.server"));
    }
  }
};

const createInstance = (
  restricted: boolean = false,
  otp: string,
  url: string | undefined,
  customHeaders?: Headers
) => {
  const headers: AxiosHeaders = new AxiosHeaders();
  const maxBodyLength = Infinity;
  if (restricted) {
    const token = getToken();
    headers.setAuthorization("Bearer " + token);
  }
  headers.setContentType("application/json");
  headers.set("ngrok-skip-browser-warning", "true");

  if (customHeaders) {
    for (const key in customHeaders) {
      headers.set(key, customHeaders[key]);
    }
  }

  const requestUrl = url ?? baseURL;

  if (otp) {
    headers.set("x-otp", otp);
  }
  const config: AxiosRequestConfig = {
    baseURL: requestUrl,
    maxBodyLength,
    headers,
    validateStatus(status) {
      return status >= 200 && status <= 307;
    },
  };

  const instance = axios.create(config);

  return instance;
};

async function sendRequest(
  method: Method,
  url: string,
  data: any = null,
  restricted: boolean = false,
  withparams?: boolean,
  otpcode?: string,
  baseUrl?: string,
  customHeaders?: Headers
): Promise<any> {
  const request: AxiosRequestConfig = {
    method,
    url,
  };
  if (method.toLowerCase() === "get") {
    request.params = data;
  } else if (withparams) {
    request.params = data;
  } else {
    request.data = data;
  }

  const requestUrl = baseUrl ?? undefined;
  const instance = createInstance(
    restricted,
    otpcode,
    requestUrl,
    customHeaders
  );
  Sentry.addBreadcrumb({ category: "request-body", data: request });

  const response = await instance
    .request(request)
    .catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        Sentry.addBreadcrumb({
          category: "error-response-body",
          data: error.response.data,
        });
      }
      if (url !== "/bridge/activate") {
        Sentry.captureException(error, {
          extra: {
            request,
            response: error.response,
            errorStringified: JSON.stringify(error),
          },
        });
      }
      if (
        (throwPostResponseUrls.includes(url) &&
          method.toLowerCase() === "post") ||
        (throwGetResponseUrls.includes(url) && method.toLowerCase() === "get")
      ) {
        responseStatus(error, url, method);
        throw error;
      } else {
        responseStatus(error, url, method);
        throw error.response;
      }
    })
    .then((response) => {
      if (response.data !== undefined) {
        const { data } = response;
        if (url === "/application-status") {
          handleApplicationStatus(data.status, data);
          return data;
        } else if (locationsUrls.includes(url)) {
          const decodedData = data.map((item: any) => decodeObjectValues(item));
          return decodedData;
        } else {
          return data;
        }
      }
    });
  Sentry.addBreadcrumb({
    category: "successful-response-body",
    data: response.data ?? "No data",
  });
  return response;
}

const sendRequestFormData = async (
  url: string,
  documentId: string,
  token: string,
  file: DocumentPickerAsset
) => {
  try {
    const response = await axios.postForm(
      url,
      {
        documentId,
        file: file.file,
      },
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    Sentry.addBreadcrumb({
      category: "successful-response-body",
      data: response.data ?? "No data",
    });
    return response;
  } catch (error: any) {
    Sentry.captureException(error, {
      extra: {
        response: error.response ?? "No response",
        errorStringified: JSON.stringify(error),
      },
    });
    responseStatus(error, url, "post");
    throw error;
  }
};

async function runAbortableAsync<T>(
  asyncFunction: () => Promise<T>,
  signal: AbortSignal
): Promise<T | null> {
  if (signal.aborted) {
    return null;
  }

  try {
    const result = await asyncFunction();
    if (signal.aborted) {
      return null;
    }
    return result;
  } catch (error) {
    if (signal.aborted) {
      console.log("Cancelled operation.");
      return null;
    }
    throw error;
  }
}

export { sendRequest, sendRequestFormData, runAbortableAsync };
